import React, { useState, useEffect } from "react";
import { listTemplates, getTemplate, seedSubTenant } from "../api/library.js";
import { useHierarchy } from "../contexts/HierarchyContext.jsx";

export default function Library({ navigate }) {
  const { subTenants, subTenantId } = useHierarchy();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [seedModal, setSeedModal] = useState(null);
  const [seedTarget, setSeedTarget] = useState("");
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  useEffect(() => {
    listTemplates().then(setTemplates).catch(console.error).finally(() => setLoading(false));
  }, []);

  const openDetail = async (slug) => {
    setDetailLoading(true);
    try {
      const data = await getTemplate(slug);
      setDetail(data);
    } catch (err) { alert("Failed to load: " + err.message); }
    finally { setDetailLoading(false); }
  };

  const handleSeed = async () => {
    if (!seedTarget || !seedModal) return;
    setSeeding(true);
    setSeedResult(null);
    try {
      const result = await seedSubTenant(seedModal, seedTarget);
      setSeedResult(result);
    } catch (err) {
      alert("Seed failed: " + err.message);
    } finally { setSeeding(false); }
  };

  if (loading) return <div style={s.loading}>Loading library...</div>;

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Library — Agent Group Templates</h1>
          <p style={s.subtitle}>Reusable seed data for specialties. Select a template to view details or seed into a sub-tenant.</p>
        </div>
      </div>

      <div style={s.grid}>
        {templates.map((t) => (
          <div key={t.slug} style={s.card} onClick={() => openDetail(t.slug)}>
            <div style={s.cardHeader}>
              <div style={s.cardName}>{t.name}</div>
              <span style={s.specialtyBadge}>{t.specialty}</span>
            </div>
            {t.description && <div style={s.cardDesc}>{t.description}</div>}
            <div style={s.cardCounts}>
              <Stat label="Physicians" value={t.counts.physicians} />
              <Stat label="Patients" value={t.counts.patients} />
              <Stat label="Slot Types" value={t.counts.slotTypes} />
              <Stat label="Triage" value={t.counts.triageProtocols} />
              <Stat label="Prep" value={t.counts.prepInstructions} />
              <Stat label="SDOH" value={t.counts.sdohQuestions} />
              <Stat label="Test Cases" value={t.counts.testCases} />
            </div>
            <button style={s.seedBtn} onClick={(e) => { e.stopPropagation(); setSeedModal(t.slug); setSeedTarget(subTenantId || ""); setSeedResult(null); }}>
              Seed Sub-Tenant
            </button>
          </div>
        ))}
      </div>

      {/* Detail Modal */}
      {(detail || detailLoading) && (
        <div style={s.overlay} onClick={() => setDetail(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            {detailLoading ? <div style={s.modalLoading}>Loading...</div> : detail ? (
              <TemplateDetail template={detail} onClose={() => setDetail(null)} />
            ) : null}
          </div>
        </div>
      )}

      {/* Seed Modal */}
      {seedModal && (
        <div style={s.overlay} onClick={() => setSeedModal(null)}>
          <div style={s.seedModalBox} onClick={(e) => e.stopPropagation()}>
            <h2 style={s.seedTitle}>Seed from "{seedModal}"</h2>
            <p style={s.seedText}>Choose a sub-tenant to populate with this template's seed data (physicians, patients, triage protocols, prep instructions, SDOH, test cases).</p>
            <select style={s.seedSelect} value={seedTarget} onChange={(e) => setSeedTarget(e.target.value)}>
              <option value="">Select a sub-tenant...</option>
              {subTenants.map((st) => (
                <option key={st._id} value={st._id}>{st.name} ({st.slug})</option>
              ))}
            </select>
            {seedResult && (
              <div style={s.seedResultBox}>
                Seeded into <strong>{seedResult.subTenant?.name}</strong>:
                {Object.entries(seedResult.counts || {}).map(([k, v]) => (
                  <span key={k} style={s.seedStat}>{k}: {v}</span>
                ))}
              </div>
            )}
            <div style={s.seedActions}>
              <button style={s.cancelBtn} onClick={() => setSeedModal(null)}>Cancel</button>
              <button
                style={seedTarget && !seeding ? s.confirmBtn : s.confirmBtnDisabled}
                onClick={handleSeed}
                disabled={!seedTarget || seeding}
              >
                {seeding ? "Seeding..." : "Seed Data"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={s.stat}>
      <div style={s.statValue}>{value}</div>
      <div style={s.statLabel}>{label}</div>
    </div>
  );
}

function TemplateDetail({ template, onClose }) {
  const t = template;
  return (
    <>
      <div style={d.header}>
        <div>
          <h2 style={d.name}>{t.name}</h2>
          <div style={d.slug}>{t.slug} — {t.specialty}</div>
        </div>
        <button style={d.closeBtn} onClick={onClose}>×</button>
      </div>
      {t.description && <div style={d.desc}>{t.description}</div>}

      <Section title={`Physicians (${(t.physicians||[]).length})`}>
        {(t.physicians||[]).map((p,i) => (
          <div key={i} style={d.item}>
            <strong>{p.physicianCode}</strong> {p.name} — {p.focusArea || (p.subspecialties||[]).join(", ")}
          </div>
        ))}
      </Section>

      <Section title={`Patients (${(t.patients||[]).length})`}>
        {(t.patients||[]).map((p,i) => (
          <div key={i} style={d.item}>
            <strong>{p.patientCode}</strong> {p.firstName} {p.lastName} — {p.condition || "—"}
          </div>
        ))}
      </Section>

      <Section title={`Slot Types (${(t.slotTypes||[]).length})`}>
        {(t.slotTypes||[]).map((st,i) => (
          <div key={i} style={d.item}>{st.type} ({st.duration}min)</div>
        ))}
      </Section>

      <Section title={`Triage Protocols (${(t.triageProtocols||[]).length})`}>
        {(t.triageProtocols||[]).map((tp,i) => (
          <div key={i} style={d.item}><strong>{tp.key}</strong> — {tp.symptom} ({tp.priority})</div>
        ))}
      </Section>

      <Section title={`Prep Instructions (${(t.prepInstructions||[]).length})`}>
        {(t.prepInstructions||[]).map((pi,i) => (
          <div key={i} style={d.item}>{pi.type} {pi.aliases?.length ? `(aliases: ${pi.aliases.join(", ")})` : ""}</div>
        ))}
      </Section>

      <Section title={`Test Cases (${(t.testCases||[]).length})`}>
        {(t.testCases||[]).map((tc,i) => (
          <div key={i} style={d.item}>
            <strong>{tc.testCaseCode}</strong> {tc.name}
            <div style={d.itemSub}>{(tc.tags||[]).join(", ")}</div>
          </div>
        ))}
      </Section>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div style={d.section}>
      <div style={d.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

const s = {
  loading: { padding: 60, textAlign: "center", color: "#999" },
  header: { padding: "20px 32px", borderBottom: "1px solid #e5e5e5" },
  title: { fontSize: 22, fontWeight: 600 },
  subtitle: { fontSize: 13, color: "#999", marginTop: 4 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340, 1fr))", gap: 16, padding: "20px 32px" },
  card: {
    border: "1px solid #e5e5e5", borderRadius: 10, padding: 20, cursor: "pointer",
    transition: "box-shadow 0.15s", background: "#fff",
  },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  cardName: { fontSize: 18, fontWeight: 600 },
  specialtyBadge: { fontSize: 11, fontWeight: 500, color: "#1d4ed8", background: "#eff6ff", padding: "2px 8px", borderRadius: 6 },
  cardDesc: { fontSize: 13, color: "#666", marginBottom: 12, lineHeight: 1.4 },
  cardCounts: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 },
  stat: { textAlign: "center", minWidth: 50 },
  statValue: { fontSize: 18, fontWeight: 700, color: "#0f0f0f" },
  statLabel: { fontSize: 10, color: "#999", textTransform: "uppercase" },
  seedBtn: {
    width: "100%", padding: "8px", background: "#0f0f0f", color: "#fff",
    border: "none", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: "pointer",
  },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, paddingTop: 30, overflowY: "auto" },
  modal: { background: "#fff", borderRadius: 12, width: 700, maxHeight: "calc(100vh - 60px)", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", marginBottom: 30 },
  modalLoading: { padding: 60, textAlign: "center", color: "#999" },
  seedModalBox: { background: "#fff", borderRadius: 12, width: 460, padding: "28px 32px", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" },
  seedTitle: { fontSize: 18, fontWeight: 600, marginBottom: 8 },
  seedText: { fontSize: 13, color: "#555", lineHeight: 1.5, marginBottom: 14 },
  seedSelect: { width: "100%", padding: "9px 12px", border: "1px solid #e5e5e5", borderRadius: 6, fontSize: 14, marginBottom: 12 },
  seedResultBox: { padding: "10px 12px", background: "#ecfdf5", borderRadius: 6, fontSize: 13, color: "#059669", marginBottom: 12 },
  seedStat: { display: "inline-block", marginLeft: 8, fontWeight: 500 },
  seedActions: { display: "flex", gap: 8, justifyContent: "flex-end" },
  cancelBtn: { padding: "8px 18px", background: "#fff", color: "#666", border: "1px solid #e5e5e5", borderRadius: 6, fontSize: 13, fontWeight: 500 },
  confirmBtn: { padding: "8px 18px", background: "#0f0f0f", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600 },
  confirmBtnDisabled: { padding: "8px 18px", background: "#999", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "not-allowed" },
};

const d = {
  header: { display: "flex", justifyContent: "space-between", padding: "20px 24px 12px", borderBottom: "1px solid #f0f0f0" },
  name: { fontSize: 20, fontWeight: 600 },
  slug: { fontSize: 12, color: "#999", marginTop: 2 },
  closeBtn: { width: 32, height: 32, border: "none", background: "transparent", fontSize: 22, color: "#999", cursor: "pointer" },
  desc: { padding: "8px 24px", fontSize: 13, color: "#666", borderBottom: "1px solid #f5f5f5" },
  section: { padding: "12px 24px", borderBottom: "1px solid #f5f5f5" },
  sectionTitle: { fontSize: 13, fontWeight: 600, marginBottom: 8 },
  item: { fontSize: 13, color: "#333", padding: "3px 0" },
  itemSub: { fontSize: 11, color: "#999" },
};
